import Price from 'components/price';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { useCustomerDetails } from 'hooks/customer/use-customer';
import { Discount, DiscountType } from 'interfaces/order';
import { FormattedMessage } from 'react-intl';

interface PropTypes {
  canToggleCredits?: boolean;
  discount: Discount | undefined;
}

/**
 * Displays discount information in cart summary with toggle controls for credits.
 *
 * Behavior:
 * - When coupon applied: Shows available credits with option to switch
 * - When no discount: Shows available credits with option to apply
 * - When credits applied: Shows applied credit amount with option to remove
 */
const SummaryPricesDiscounts: React.FC<PropTypes> = ({ canToggleCredits = false, discount }) => {
  const { setUseCredits, setCouponCode } = useCart();
  const { customer } = useAuth();
  const { details } = useCustomerDetails(customer?.customerId);

  const hasCreditBalance = details?.creditBalance?.balance && details.creditBalance.balance > 0;

  // Show available credits when coupon is applied or no discount at all
  const shouldShowAvailableCredits =
    canToggleCredits &&
    hasCreditBalance &&
    ((discount && discount.type === DiscountType.COUPON) || !discount);

  if (shouldShowAvailableCredits && details?.creditBalance) {
    return (
      <div
        className={'flex justify-between text-sm opacity-75'}
        data-test="available-credits-container"
      >
        <div className="text-sm">
          <FormattedMessage id={`cart:discount:Credit`} />
          <button
            data-test="toggle-use-credits-button"
            onClick={() => {
              setUseCredits(true);
              setCouponCode('');
            }}
            className="focus-default hover:text-primary-main ml-1 inline-block underline"
          >
            <FormattedMessage id="express-checkout:summary-prices:use-discount" />
          </button>
        </div>
        <Price cents value={details.creditBalance.balance} data-test="available-credit-balance" />
      </div>
    );
  }

  // Only render credit discount
  if (!discount || discount.type !== DiscountType.CREDIT || discount.totalDiscount <= 0) {
    return null;
  }

  return (
    <div className="flex justify-between text-sm" data-test="applied-credits-container">
      <div className="text-sm">
        <FormattedMessage id={`cart:discount:${discount.type}`} />
        {canToggleCredits && setUseCredits && (
          <button
            data-test="toggle-remove-credits-button"
            onClick={() => {
              setUseCredits(false);
              setCouponCode('');
            }}
            className="focus-default hover:text-primary-main ml-1 inline-block underline"
          >
            <FormattedMessage id="express-checkout:summary-prices:not-use-discount" />
          </button>
        )}
      </div>
      {typeof discount.fixedValue === 'number' && (
        <Price cents value={-discount.fixedValue} data-test="applied-credit-fixed-value" />
      )}
      {typeof discount.fixedValue !== 'number' && typeof discount.totalDiscount === 'number' && (
        <Price cents value={-discount.totalDiscount} data-test="applied-credit-total-discount" />
      )}
    </div>
  );
};

export default SummaryPricesDiscounts;
